/**
 * FileTreeDrag hook — drag files to move them between folders.
 * Sends "move_file" event with {file_id, target_folder}.
 *
 * Listeners are tracked in `_bound` and removed before every re-setup:
 * `updated()` fires on each LiveView patch, and re-adding handlers without
 * teardown stacks them (a single drop would push N duplicate "move_file"
 * events after N patches).
 */
export default {
  mounted() { this._setup() },
  updated() {
    this._teardown()
    this._setup()
  },
  destroyed() { this._teardown() },

  _bind(el, type, fn) {
    el.addEventListener(type, fn)
    this._bound.push({ el, type, fn })
  },

  _teardown() {
    (this._bound || []).forEach(({ el, type, fn }) => el.removeEventListener(type, fn))
    this._bound = []
  },

  _setup() {
    this._bound = this._bound || []
    const container = this.el
    let draggedFileId = null

    // Make files draggable
    container.querySelectorAll("[data-file-id]").forEach(el => {
      el.setAttribute("draggable", "true")

      this._bind(el, "dragstart", (e) => {
        draggedFileId = el.dataset.fileId
        e.dataTransfer.setData("text/plain", draggedFileId)
        e.dataTransfer.effectAllowed = "move"
        el.style.opacity = "0.4"
        e.stopPropagation()
      })

      this._bind(el, "dragend", () => {
        el.style.opacity = ""
        draggedFileId = null
      })
    })

    // Make folders drop targets
    container.querySelectorAll("[data-folder-path]").forEach(folder => {
      this._bind(folder, "dragover", (e) => {
        e.preventDefault()
        e.stopPropagation()
        e.dataTransfer.dropEffect = "move"
        folder.style.backgroundColor = "var(--accent-primary)"
        folder.style.opacity = "0.15"
      })

      this._bind(folder, "dragleave", (e) => {
        e.stopPropagation()
        folder.style.backgroundColor = ""
        folder.style.opacity = ""
      })

      this._bind(folder, "drop", (e) => {
        e.preventDefault()
        e.stopPropagation()
        folder.style.backgroundColor = ""
        folder.style.opacity = ""
        const fileId = draggedFileId || e.dataTransfer.getData("text/plain")
        if (fileId) {
          this.pushEvent("move_file", {
            file_id: fileId,
            target_folder: folder.dataset.folderPath
          })
        }
      })
    })

    // Root area = move to root (no folder)
    this._bind(container, "dragover", (e) => {
      // Only highlight if dropping directly on container, not on a folder
      if (!e.target.closest("[data-folder-path]") && !e.target.closest("[data-file-id]")) {
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
      }
    })

    this._bind(container, "drop", (e) => {
      if (!e.target.closest("[data-folder-path]")) {
        e.preventDefault()
        const fileId = draggedFileId || e.dataTransfer.getData("text/plain")
        if (fileId) {
          this.pushEvent("move_file", { file_id: fileId, target_folder: "" })
        }
      }
    })
  }
}
